import React from 'react';
import isEqual from 'lodash.isequal';
export const NostoContext = React.createContext(null);
export class NostoTagging extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            data: this.props.data,
            elements: []
        };
        this.reject = null;
        this.registerPromise = (promise) => {
            this.setState({ data: null });
            const rejectablePromise = new Promise((resolve, reject) => {
                this.reject = reject;
                Promise.resolve(promise).then(resolve);
            }).then(data => {
                this.setState({ data });
                return data;
            }, () => console.log('Nosto', this.props.id, 'promise was rejected'));
            this.context.registerTagging(rejectablePromise, this.props.id);
        };
    }
    componentDidMount() {
        const { promise, data, elementClicked, id } = this.props;
        if (promise) {
            this.registerPromise(promise);
        }
        else if (data) {
            this.context.registerTagging(Promise.resolve(data), id);
        }
        elementClicked && this.context.registerElementClicked(elementClicked);
    }
    shouldComponentUpdate(nextProps, nextState) {
        return (this.props.promise !== nextProps.promise ||
            this.props.elementClicked !== nextProps.elementClicked ||
            this.props.children !== nextProps.children ||
            this.state.data !== nextState.data);
    }
    componentDidUpdate(prevProps) {
        const { promise, data, elementClicked, id } = this.props;
        // If a promise was provided, make sure we await it
        if (promise) {
            if (promise !== prevProps.promise) {
                this.reject && this.reject();
                this.registerPromise(promise);
            }
        }
        else if (data) {
            // If a data object is passed, make a deep equal comparison to see if we should update the tagging or not
            if (!isEqual(data, prevProps.data)) {
                this.setState({ data });
                this.context.registerTagging(Promise.resolve(data), id);
            }
        }
        if (elementClicked && elementClicked !== prevProps.elementClicked) {
            this.context.registerElementClicked(elementClicked);
        }
    }
    componentWillUnmount() {
        this.reject && this.reject();
    }
    render() {
        const { data } = this.state;
        if (data) {
            return this.props.children(data);
        }
        else {
            return null;
        }
    }
}
NostoTagging.contextType = NostoContext;
NostoTagging.defaultProps = {
    promise: null,
    data: null
};
class NostoProvider extends React.Component {
    constructor(props) {
        super(props);
        this.registerElement = (elementId) => {
            this.setState(state => ({ elements: [...state.elements, elementId] }));
            this.props.debug && console.log('Register element', elementId);
        };
        this.unregisterElement = (elementId) => {
            this.setState(state => ({
                elements: state.elements.filter(id => id !== elementId)
            }));
            this.props.debug && console.log('Unregister element', elementId);
        };
        this.registerTagging = (promise, id) => {
            this.setState(state => ({ promises: [...state.promises, promise] }));
            this.props.debug && console.log('Register tagging', id);
            this.props.debugTagging &&
                promise.then(data => console.log('Tagging', id, 'resolved with data:', data));
        };
        this.registerElementClicked = (elementId) => {
            this.setState({ elementClicked: elementId });
            this.props.debug && console.log('Register element clicked', elementId);
        };
        this.addEventListener = (event, listener) => window.nostojs((api) => api.listen(event, listener));
        this.resendCartTagging = () => {
            window.nostojs((api) => api.resendCartTagging());
            this.props.debug && console.log('Resend cart tagging');
        };
        this.reportAddToCart = (productId, elementId) => {
            window.nostojs((api) => api.reportAddToCart(productId, elementId));
            this.props.debug && console.log('Report add to cart');
        };
        this.state = {
            elements: [],
            promises: [],
            elementClicked: null,
            registerElement: this.registerElement,
            unregisterElement: this.unregisterElement,
            registerElementClicked: this.registerElementClicked,
            registerTagging: this.registerTagging,
            addEventListener: this.addEventListener,
            resendCartTagging: this.resendCartTagging,
            reportAddToCart: this.reportAddToCart
        };
        if (!props.id) {
            throw new Error('You need to provide Nosto id as a prop.');
        }
        // make sure to only run this in DOM land
        if (typeof window !== 'undefined') {
            // Set up Nosto queue to support calls before script has loaded
            window.nostojs =
                window.nostojs ||
                    function (cb) {
                        (window.nostojs.q = window.nostojs.q || []).push(cb);
                    };
            // Make sure Nosto doesn't try to load recommendations before we've rendered/hydrated everything
            window.nostojs((api) => api.setAutoLoad(false));
            // Add nosto script to document and initialize it
            const tag = document.createElement('script');
            tag.type = 'text/javascript';
            tag.async = true;
            tag.innerHTML = `(function(){function a(a){var b,c,d=window.document.createElement("iframe");d.src="javascript:false",(d.frameElement||d).style.cssText="width: 0; height: 0; border: 0";var e=window.document.createElement("div");e.style.display="none";var f=window.document.createElement("div");e.appendChild(f),window.document.body.insertBefore(e,window.document.body.firstChild),f.appendChild(d);try{c=d.contentWindow.document}catch(g){b=document.domain,d.src="javascript:var d=document.open();d.domain='"+b+"';void(0);",c=d.contentWindow.document}return c.open()._l=function(){b&&(this.domain=b);var c=this.createElement("scr".concat("ipt"));c.src=a,this.body.appendChild(c)},c.write("<bo".concat('dy onload="document._l();">')),c.close(),d}var b="nostojs";window[b]=window[b]||function(a){(window[b].q=window[b].q||[]).push(a)},window[b].l=new Date;var c=function(d,e){if(!document.body)return setTimeout(function(){c(d,e)},30);e=e||{},window[b].o=e;var f=document.location.protocol,g=["https:"===f?f:"http:","//",e.host||"connect.nosto.com",e.path||"/include/",d].join("");a(g)};window[b].init=c})();`;
            tag.id = 'nosto-script';
            document.getElementsByTagName('body')[0].appendChild(tag);
            window.nostojs.init(props.id);
        }
    }
    componentDidUpdate(_prevProps, prevState) {
        const { promises, elements, elementClicked } = this.state;
        const promisesChanged = promises !== prevState.promises;
        const elementsChanged = elements !== prevState.elements;
        const elementClickedChanged = elementClicked !== prevState.elementClicked;
        const shouldUpdate = (promisesChanged && promises.length) ||
            elementsChanged ||
            (elementClickedChanged && elementClicked);
        if (shouldUpdate) {
            // Await any promises so we are sure we have the correct data in the markup before we send tagging to Nosto
            Promise.all(promises).then(data => {
                // Make sure we haven't added any new promises since we started waiting for promises
                // If we have, componentDidUpdate would be triggered again and we would wait again
                if (promises !== this.state.promises) {
                    this.props.debug &&
                        console.log('Promises added while waiting! Abort.');
                    return;
                }
                const { elements, elementClicked } = this.state;
                // Filter away null responses
                const filteredPromises = data.filter(Boolean);
                if (filteredPromises.length || elements.length || elementClicked) {
                    this.props.debug && console.log('Ready to send tagging!');
                }
                else {
                    this.props.debug && console.log('No tagging to send.');
                    return;
                }
                if (elements.length || elementClicked) {
                    // We have recommendation slots to fill or showing a product based on a click of a Nosto recommendation
                    window.nostojs((api) => api.loadRecommendations(elementClicked
                        ? { markNostoElementClicked: elementClicked }
                        : {}));
                }
                else {
                    // We have no recommendation slots to fill
                    window.nostojs((api) => api.sendTagging());
                }
                this.setState({
                    promises: [],
                    elementClicked: null
                });
            }, () => this.setState({ promises: [], elementClicked: null }));
        }
    }
    componentWillUnmount() {
        // Delete the injected script
        const nostoScript = document.getElementById('nosto-script');
        if (nostoScript) {
            nostoScript.remove();
        }
    }
    render() {
        return (React.createElement(NostoContext.Provider, { value: this.state }, this.props.children));
    }
}
export default NostoProvider;
//# sourceMappingURL=Provider.js.map